import React from 'react';
import filterConfig from '../widgets';
import type {
  ChannelValueType,
  FilterKeys,
  SelectDefaultOptionsType,
} from '../types';
import { LocaleType } from '@/pages/opinion/types/common';
import { urlParamsFormatMap } from '../constants';
import moment from 'moment';
import { dateWhiteUserList } from '../constants/date';
import { targetNumMap } from '../constants/engagementPerformance';
import _ from 'lodash-es';
import { getShortUrlParse } from '@op/services';
import { channelSplitFlag } from '../constants/channel';
import DOMPurify from 'dompurify';
import { formatDateTimeRange } from './time';
import { channelInfo } from '@op/components/ChannelLabel';

const enUS = {
  search_text: 'Search',
  tab_common: 'Common',
  tab_popular: 'Key Country',
  title_overall: 'Overall',
  title_region: 'Region',
  title_market: ' Market',
  title_popular: 'Key',
  title_country: ' Country',
  select_all: 'Select All',
  clear_all: 'Clear All',
  no_data: 'No data yet.',
  global: 'Global',
  global_except_china: 'Global except China mainland',
  global_no_data_text: 'Including comments from game store and social media.',
  unidentified_no_data_text:
    'Including all comments by keywords monitoring and part by accounts monitoring in social media which can not be distinguished from markets.',
  global_message:
    'Global includes reviews fromgame store and social media, which cannot be distinguished from regions/market.',
};

const zhCN = {
  search_text: '搜索',
  tab_common: '全部区域/国家',
  tab_popular: '重点关注国家',
  title_overall: '聚合数据',
  title_region: '区域',
  title_market: '市场',
  title_popular: '重点关注',
  title_country: '国家',
  select_all: '全选',
  clear_all: '清空',
  no_data: '无匹配项',
  global: '全球',
  global_except_china: '全球除中国大陆',
  global_no_data_text: '包括来自游戏商店和社交媒体的评论。',
  unidentified_no_data_text:
    '包括所有无法与市场区分开来的社交媒体中的关键字监控和部分帐户监控的评论。',
  global_message:
    '全球包括来自游戏商店和社交媒体的评论，无法与地区/市场区分开来。',
};

const localeMap = {
  'en-US': enUS,
  'zh-CN': zhCN,
};

let locale: 'en-US' | 'zh-CN' = 'en-US';

export const getLocaleText = (key: string) => {
  const map: { [key: string]: string } = localeMap[locale];
  return map[key] || key;
};

export const setLocale = (newLocale: 'en-US' | 'zh-CN') => {
  locale = newLocale;
};

/**
 * 标记指定关键字高亮（以关键字的特殊字符分隔后的数组单个值为单位，不区分大小写）
 * @param {string} text 需要处理的原字符串
 * @param {string} key  需要标记的字符串关键字
 */
export function highlight(text: string | JSX.Element, key: string) {
  if (key && text && typeof text === 'string' && typeof key === 'string') {
    const newText = text.replace(
      new RegExp(key, 'gi'), // 'g' 表示全局搜索，'i' 表示不区分大小写
      (match) => `<Fragment style="color: #006EFF;">${match}</Fragment>`,
    );
    return (
      <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(newText) }} />
    );
  }
  return text;
}

export const getAppointKey = (
  key: string,
  appointKey: 'requestKey' | 'interfaceKey' | 'params' = 'requestKey',
) => {
  const current: any = filterConfig[key as FilterKeys] || {};
  if (typeof current !== 'function') {
    return current?.[appointKey];
  }

  return null;
};

/**
 * 获取需要请求的key值
 * @param filter 用户需要使用的过滤器个数
 * @returns
 */
export function getRequestType(
  filter: Array<FilterKeys>,
  excludeKey: Array<string>,
  response: { [key: string]: any },
  otherRequestType: Array<string>,
): { keys: Array<string>; params: any } {
  let result: Array<string> = [];
  let otherParams: { [key: string]: any } = {};
  filter.forEach((key) => {
    const requestKey = getAppointKey(key);
    // 不需要请求数据
    if (!requestKey || excludeKey.includes(key)) return;

    // 已经请求数据
    const interfaceKey = getAppointKey(key) || requestKey;
    if (interfaceKey) {
      const keys = Array.isArray(interfaceKey) ? interfaceKey : [interfaceKey];
      const has = keys.every((key) => {
        return typeof response?.[key] === 'boolean' ? true : Boolean(response?.[key]);
      });

      if (has) return;
    }

    const params = getAppointKey(key, 'params') || {};
    otherParams = { ...otherParams, ...params };

    if (typeof requestKey === 'string') {
      return result.push(requestKey);
    }
    result = result.concat(requestKey);
  });

  otherRequestType.forEach((key) => {
    if (!_.has(response, key)) {
      result.push(key);
    }
  });

  return { keys: result, params: otherParams };
}

export function getOtherParams(
  filter: Array<FilterKeys>,
  excludeKey: Array<string>,
  response: { [key: string]: any },
  otherRequestType: Array<string>,
): Array<string> {
  let result: Array<string> = [];
  filter.forEach((key) => {
    const requestKey = getAppointKey(key);
    // 不需要请求数据
    if (!requestKey || excludeKey.includes(key)) return;

    // 已经请求数据
    const interfaceKey = getAppointKey(key) || requestKey;
    if (interfaceKey) {
      const keys = Array.isArray(interfaceKey) ? interfaceKey : [interfaceKey];
      const has = keys.every((item) => {
        return Boolean(response?.[item]);
      });

      if (has) return;
    }

    if (typeof requestKey === 'string') {
      return result.push(requestKey);
    }
    result = result.concat(requestKey);
  });

  otherRequestType.forEach((key) => {
    if (!_.has(response, key)) {
      result.push(key);
    }
  });

  return result;
}

export const getRequestFuncValue = (key: string, data: any) => {
  const current: any = filterConfig[key as FilterKeys] || {};
  if (typeof current !== 'function') {
    if (current.interfaceKey) return null;
    const { condCacheKey } = current || {};
    if (condCacheKey) return data[condCacheKey];
  }

  return null;
};
/**
 * 获取需要请求的func
 * @param filter 用户需要使用的过滤器个数
 * @returns
 */
export function getRequestFuncList(
  filter: Array<FilterKeys>,
  response: { [key: string]: any },
): Array<Promise<any> | any> {
  let result: Promise<any> | any = [];
  filter.forEach((key) => {
    const current: any = filterConfig[key as FilterKeys] || {};
    if (typeof current !== 'function' && current.condCacheKey) {
      const { needCache = true, condCacheKey, requestFunc } = current || {};
      if (!condCacheKey) {
        return console.error('缺少condCacheKey, 无法映射到组件内部');
      }
      const cache = response[condCacheKey];
      // 会缓存，并且缓存已经有了，就不在进行添加
      if (needCache && cache) return;
      result = result.concat(requestFunc);
    }
  });
  return result;
}

/**
 * 将接口返回的数据处理成我们希望传入组件的数据
 * @param filter             用户需要使用的过滤器个数
 * @param filterOptionsData  过滤器接口返回的数据
 * @returns
 */
export function filterOptionsAssemble(
  filter: Array<FilterKeys>,
  filterOptionsData: any,
  defaultOptions: SelectDefaultOptionsType,
) {
  let result: { [key in FilterKeys]?: any } = {};
  filter.forEach((key) => {
    // 如果默认选项中有数据，那么就用默认数据
    if (defaultOptions[key]) {
      return (result[key] = defaultOptions[key]);
    }

    // 处理单独请求的情况
    const functionValue = getRequestFuncValue(key, filterOptionsData);
    if (functionValue) {
      return (result[key] = functionValue);
    }

    const requestKey = getAppointKey(key);
    // 主要看接口返回的key值，如果接口返回的key没有，就用requestKey
    const interfaceKey = getAppointKey(key, 'interfaceKey') || requestKey;

    // 没有requestKey说明选项并不需要请求得到数据
    if (!requestKey && !interfaceKey) return;

    // 如果是字符串，说明这个组件并不需要多个数据
    if (typeof interfaceKey === 'string') {
      // 默认以过滤器配置的key为主
      return (result[key] = filterOptionsData[interfaceKey]);
    }

    // 数组，就循环将key替换了interfaceKey
    let subResult: { [key: string]: any } = {};
    (interfaceKey || []).forEach((rKey: string) => {
      subResult[rKey] = filterOptionsData[rKey];
    });

    // 判断官号中是否有直播类型
    if (key === 'monitor') {
      let obj = subResult?.monitor_by_account_category?.monitory || {};
      let flag = false;
      for (const key in obj) {
        const ele = obj[key];
        if (ele && !flag) {
          flag = ele.some((item: any) => item.channel.includes('live'));
        }
      }
      result.reviewType = flag;
    }

    result[key] = subResult;
  });
  return result;
}

/**
 * 处理数据为数组，并且可以渲染的那种
 * @param languageOptions 对象
 * @param filter 过滤的字段
 * @returns
 */
export function dealwithLanguage(
  languageOptions: { [key: string]: [string, string, string] },
  filter?: string,
  lang: LocaleType = 'zh-CN',
) {
  if (!languageOptions) {
    return [];
  }
  const result: Array<{ text: string; value: string }> = [];
  for (const key in languageOptions) {
    if (Object.prototype.hasOwnProperty.call(languageOptions, key)) {
      const [sKey, enText, cnText] = languageOptions[key];
      let text = lang === 'zh-CN' ? cnText : enText;
      // 过滤部分数据
      if (filter) {
        const flag = !text.toLowerCase().includes(filter.toLowerCase());
        if (flag) continue;
      }
      result.push({
        text: text,
        value: sKey,
      });
    }
  }

  return result;
}

/**
 * 处理数据为数组，并且可以渲染的那种
 * @param languageOptions 对象
 * @param filter 过滤的字段
 * @returns
 */
export function dealwithVideoTag(
  videoTagOptions: Array<{ label: string; label_ch: string; label_en: string }>,
  filter?: string,
  lang: LocaleType = 'zh-CN',
) {
  if (!videoTagOptions) {
    return [];
  }
  const result: Array<{ text: string; value: string }> = [];

  videoTagOptions.forEach(({ label, label_ch, label_en }: any) => {
    let text = lang === 'zh-CN' ? label_ch : label_en;
    // 过滤部分数据
    if (filter) {
      const flag = !text.toLowerCase().includes(filter.toLowerCase());
      if (flag) return;
    }
    result.push({
      text: text,
      value: label,
    });
  });

  return result;
}

/**
 * 处理数据为数组，并且可以渲染的那种
 * @param hashtagOptions 对象
 * @param filter 过滤的字段
 * @returns
 */
export function dealwithHashtag(
  hashtagOptions: Array<string>,
  filter?: string,
) {
  if (!hashtagOptions) {
    return [];
  }
  const result: Array<{ text: string; value: string }> = [];
  hashtagOptions.forEach((item: string) => {
    // 过滤部分数据
    if (filter) {
      const flag = !item.toLowerCase().includes(filter.toLowerCase());
      if (flag) return;
    }
    result.push({
      text: `#${item}`,
      value: item,
    });
  });

  return result;
}

/**
 * 处理数据，为渲染保持一致性，并且可以渲染的那种
 * @param languageOptions 对象
 * @param filter 过滤的字段
 * @returns
 */
export function dealwithChannel(
  channelOptions: {
    comments: Array<string>;
    news: Array<string>;
    social: Array<string>;
    security: Array<string>;
  },
  filter?: string,
) {
  const keys = Object.keys(channelOptions || {});
  const result: { [key in any]: { text: string; value: string }[] } = {};
  const valueMap: { [key in any]: string[] } = {};

  keys.forEach((key: any) => {
    valueMap[key] = [];
    result[key] = [];
  });

  _.forEach(channelOptions, (item: any, key: string) => {
    const newKey = key as 'comments' | 'news' | 'social' | 'security';
    const element = item || [];
    for (const text of element) {
      const realText = text.toLocaleLowerCase();
      // 过滤部分数据
      if (filter) {
        const filterLower = filter.toLowerCase();
        if (newKey !== 'news') {
          // 非 news 渠道：通过 channelInfo 获取渠道名称进行匹配
          const channelName = channelInfo(realText)?.name?.toLowerCase();
          if (!channelName?.includes(filterLower)) continue;
        } else {
          // news 渠道：直接用原始文本匹配
          if (!text?.toLowerCase().includes(filterLower)) continue;
        }
      }


      if (valueMap[newKey].includes(realText)) continue;
      result[newKey]?.push({
        text,
        value: key + channelSplitFlag + realText,
      });
      valueMap[newKey].push(realText);
    }
  });

  return {
    options: result,
    valueMap,
  };
}

export function dealwithChannelToResult(
  channelList: Array<string>,
  isAll: boolean,
  valueMap: { [key in any]: string[] } = {},
) {
  const result: {
    channel_comments: Array<string>;
    channel_news: Array<string>;
    channel_social: Array<string>;
    channel_security: Array<string>;
    exclude_channels?: Array<string>;
  } = {
    channel_comments: [],
    channel_news: [],
    channel_social: [],
    channel_security: [],
  };

  let deleteKey: Array<string> = ['exclude_channels'];
  const keyMap: { [key in ChannelValueType]: string } = {
    channel_comments: 'comments',
    channel_news: 'news',
    channel_social: 'social',
    channel_security: 'security',
  };

  if (isAll) return { result, deleteKey };

  // 将数据分解到对象中
  if (Array.isArray(channelList)) {
    channelList
      .filter((item) => typeof item === 'string')
      .forEach((item) => {
        const [iKey, value] = item.split(channelSplitFlag);
        const key: ChannelValueType = `channel_${iKey}` as any;
        if (!result[key]) {
          result[key] = [];
        }
        result[key].push(value);
      });
  }

  for (const newKey in result) {
    const key = newKey as ChannelValueType;
    const element = result[key];
    const valueKey = keyMap[key];
    const allValue = valueMap[valueKey] || [];

    if (element.length === allValue.length) {
      result[key] = [];
    } else if (element.length === 0) {
      result[key] = ['-99999'];
    }

    if (key === 'channel_news' && element.length > allValue.length / 2) {
      // 通过该方法添加排除渠道，然后通过 finallySpecialValue 方法设置为空，保证回显操作
      result.exclude_channels = allValue.filter(
        (key) => !element.includes(key),
      );
      deleteKey = [];
    }
  }
  return { result, deleteKey };
}

export const dealwithToLowerCase = (filter: any) => {
  const keys = [
    'social_media_by_keyword',
    'social_media_by_account',
    'region',
    'topic',
  ];
  return keys.reduce((pre, cur) => {
    const value = filter?.[cur];
    if (value) {
      return {
        ...pre,
        [cur]: (filter?.[cur] || []).map((key: string) =>
          key?.toLocaleLowerCase?.(),
        ),
      };
    }
    return pre;
  }, {});
};

export function mergeChannelParams(params: any) {
  const { channel, channel_type } = params || {};
  const obj: any = {};
  if (channel_type) {
    const key = `channel_${channel_type}`;
    const paramsChannel = params?.[key] || [];
    obj[key] = uniqueArray([...paramsChannel, ...(channel || [])]);
  }

  return obj;
}

export const dealwithParams = (params: any) => {
  const lowerCaseData = dealwithToLowerCase(params);
  const obj = mergeChannelParams(params);
  return { ...lowerCaseData, ...obj };
};

// 获取全局的用户user_id
export function getSessionGlobalUserId() {
  const sessionUserInfo = sessionStorage.getItem('userID') || '';
  return sessionUserInfo;
}

/**
 * 转为简单对象
 * {
 *   key: valueKey
 * }
 * @param arrayInfo 数组
 * @param key  key对应的键
 * @param valueKey 值对应的键
 * @returns
 */
export function arrayToSimpleObj(
  arrayInfo: Array<any>,
  valueKey: string = 'text',
  key: string = 'value',
) {
  try {
    return (arrayInfo || [])?.reduce((obj, current) => {
      const newKey: any = current?.[key];
      return {
        ...obj,
        [newKey]: current?.[valueKey],
      };
    }, {});
  } catch (error) {
    console.log('数组转对象错误', error);
  }
  return {};
}

/**
 * 数组转对象
 * @param arrayInfo  数组对象
 * @param key
 * string : 取的arrayInfo中对象的值
 * function: 接收一个参数[arrayInfo数组对象中对象]，处理后返回key值
 *
 * eg：
 *   let arrayInfo = [{strKey: "info",fnKey: "name_info"}]
 *   let a = arrayToObj(arrayInfo, "strKey");
 *   a => {info: {strKey: "info",fnKey: "name_info"}}
 *   let b = arrayToObj(arrayInfo, (item) => item.split("_")[0])
 *   b => {name: {strKey: "info",fnKey: "name_info"}}
 * @returns
 */
export function arrayToObj(
  arrayInfo: Array<any>,
  key: string | ((param: any) => string),
) {
  try {
    return arrayInfo.reduce((obj, current) => {
      let newKey: any = typeof key === 'function' ? key(current) : current[key];
      return {
        ...obj,
        [newKey]: current,
      };
    }, {});
  } catch (error) {
    console.log('数组转对象错误', error);
  }
  return {};
}

// 数组去重
export function uniqueArray<T>(arr: Array<T>) {
  return Array.from(new Set(arr));
}

/**
 * 比较两个数组是否相等
 *
 * 此函数通过检查两个数组的唯一元素是否完全相同来确定数组是否相等它首先确保两个数组都存在，
 * 然后将数组中的元素通过uniqueArray函数去重，之后比较去重后的数组长度如果长度相同，
 * 则进一步检查每一个元素是否包含在另一个数组中此方法适用于任何类型的数组
 *
 * @param arr1 第一个数组，可以是任何类型的数组
 * @param arr2 第二个数组，可以是任何类型的数组
 * @returns 如果两个数组相等，则返回true；否则返回false
 */
export function compareArraysEqual<T>(
  arr1: Array<T>,
  arr2: Array<T>,
  isVague = false,
) {
  // 两边随便谁不存在，都代表不相同
  if (!arr1 || !arr2) {
    return false;
  }

  const localArr1 = uniqueArray(arr1);
  const localArr2 = uniqueArray(arr2);

  // 如果长度不同，那肯定就不同
  if (localArr1.length !== localArr2.length) {
    return false;
  }

  return localArr1.every((item) => {
    if (isVague) {
      if (typeof item === 'string') {
        // @ts-ignore
        return localArr2.includes(item) || localArr2.includes(Number(item));
      }
      if (typeof item === 'number') {
        // @ts-ignore
        return localArr2.includes(item) || localArr2.includes(String(item));
      }
    }
    return localArr2.includes(item);
  });
}

/**
 * 首字母大写
 * @param content 内容
 * @returns
 */
export function initialUpperCase(content: string): string {
  if (typeof content !== 'string') {
    return content;
  }
  try {
    return content[0]?.toUpperCase() + content.substr(1); // 首字母大写
  } catch (error) {
    return content;
  }
}

/**
 * 组装通用的后台信息返回对象。
 *
 * 此函数用于构建一个包含特定字段的对象，这些字段包括实际的值、固定的头部信息和扩展信息。
 * 如果需要，还可以对值进行处理，例如将空数组转换为包含特定值的数组。
 *
 * @param value 原始数据对象，其键值对代表了数据的具体内容。
 * @param fixedHeader 固定的头部信息，可以是对象或JSX元素，用于展示在数据前面的固定信息。
 * @param options 配置选项对象，
 *        `needChangeEntity`:用于指示是否需要转换实体。
 *        `ext`: 扩展信息，用于承载额外的数据或元信息。
 * @returns 返回一个对象，包含处理后的值、固定的头部信息和扩展信息。
 */
export function commonBackInfo(
  value: { [key: string]: any },
  fixedHeader: { [key: string]: any } | JSX.Element,
  options: {
    needChangeEntity?: boolean;
    isNumber?: boolean;
    ext?: any;
    defaultValue?: any;
    defaultAllValue?: any;
    isAll?: boolean;
    excludeKeys?: Array<string>;
  } = {},
) {
  // 默认配置为不需要转换实体。
  const {
    needChangeEntity,
    ext,
    isNumber,
    defaultValue,
    defaultAllValue = [],
    isAll,
    excludeKeys,
  } = Object.assign(
    {
      needChangeEntity: false,
      isNumber: false,
      isAll: false,
      excludeKeys: [],
    },
    options,
  );

  const result = { ...value };

  // 如果需要转换实体，则对值进行处理。
  if (needChangeEntity) {
    _.forEach(value, (element: string | any[], key: string) => {
      // 对数组类型的元素进行处理，为空数组时替换为特定值的数组。
      if (Array.isArray(element) && !excludeKeys.includes(key)) {
        const nodata = defaultValue || (isNumber ? -99999 : '-99999');
        const nodataArr = Array.isArray(nodata) ? nodata : [nodata];
        if (isAll) {
          result[key] = defaultAllValue;
        } else if (element.length === 0) {
          result[key] = nodataArr;
        } else {
          result[key] = element;
        }
      }
    });
  }

  // 返回处理后的值、固定的头部信息和扩展信息组成的对象。
  return { value: result, fixedHeader, ext };
}

// 不分大小写都模糊匹配
export const upperAndLowerFuzzyQuery = (list: Array<any>, keyword: string) => {
  return list.filter((item) => {
    const text = JSON.parse(JSON.stringify(item?.text || '')).toLowerCase();
    const key = keyword.toLowerCase();
    return text.includes(key);
  });
};

export function getUrlParams(history: any) {
  const { query } = history.location || {};

  let obj: any = Object.keys(query || {}).reduce((pre: any, cur: string) => {
    const formatFunc = urlParamsFormatMap[cur];
    const value = query[cur];
    if (value && formatFunc) {
      const formatValue = formatFunc(value);
      // 不沿用原key的就要返回_flag，其他的数据作为存入对象
      if (formatValue?._flag) {
        const { _flag, ...otherInfo } = formatValue;
        return { ...pre, ...otherInfo };
      }
      return { ...pre, [cur]: formatValue };
    }
    return pre;
  }, {});
  const { shortid, start_time, end_time, date_type, ...otherInfo } = obj;
  // 这两个有一个时间不正确就去掉这个参数
  if (!start_time || !end_time) {
    obj = otherInfo;
  } else {
    // 格式化时间为 YYYY-MM-DD HH:mm:ss 格式
    const formattedTime = formatDateTimeRange(start_time, end_time, date_type);
    obj.start_time = formattedTime.start_time;
    obj.end_time = formattedTime.end_time;
  }
  // 如果dateType有，但是不对，就设置默认值
  if (
    date_type &&
    !['daily', 'hourly', 'weekly', 'monthly'].includes(date_type)
  ) {
    obj.date_type = 'daily';
  }

  if (Object.values(obj).length > 0) {
    return { ...obj, ...dealwithParams(obj) };
  }

  return null;
}

const shortStorageFilterKey = 'shortStorageFilterKey';

export function saveShortUrlInfo(key: string, value: string) {
  sessionStorage.setItem(shortStorageFilterKey + key, value);
}

function getStorageShortUrlInfo(key: string) {
  try {
    const info = sessionStorage.getItem(shortStorageFilterKey + key);
    if (info === null) return null;
    return JSON.parse(info);
  } catch (error) {
    return null;
  }
}

async function getShortInfo(shortId: string) {
  const data = getStorageShortUrlInfo(shortId);
  if (data !== null) return data;

  const filter = await getShortUrlParse(shortId);
  saveShortUrlInfo(shortId, JSON.stringify(filter));

  return filter;
}

/**
 * 获取短链参数，并处理完成时间上的替换
 * @param history
 * @param shortid
 * @param defaultTime
 * @param shortIdPriority shortId优先
 * @returns
 */
export async function getShortUrlParams(
  history: any,
  shortid?: string,
  defaultTime: any = {},
  shortIdPriority = true,
) {
  const { query } = history.location || {};
  const id = shortid || query?.shortid;
  if (!id || typeof id !== 'string') return null;

  const [shortId, sign] = id.split('-');

  const data = await getShortInfo(shortId);

  /**
   * 取出defaultValue的时间参数进行处理
   * @param data
   * @returns
   */
  function addDefaultValue(data: any, priority = shortIdPriority) {
    if (priority) {
      return data;
    }

    const { start_time: localStartTime, end_time: localEndTime } =
      defaultTime || {};

    if (localStartTime && localEndTime) {
      return {
        ...data,
        start_time: localStartTime,
        end_time: localEndTime,
      };
    }
    return data;
  }

  // 去掉时间参数
  if (sign === '1') {
    const { start_time, end_time, date_type, ...otherInfo } = data;
    return addDefaultValue(otherInfo, false);
  }

  return addDefaultValue(data);
}

/**
 * 判断时间跨度是否超3年
 * @param timeValue
 * @returns true: 超3年， false：未超3年
 */
export const getTimeLimitation = (
  timeValue: Array<string>,
  defaultNum = 3,
  defaultUnit: moment.DurationInputArg2 = 'y',
) => {
  return moment(
    moment(timeValue[1]).subtract(defaultNum, defaultUnit).format('YYYY-MM-DD'),
  ).isAfter(moment(timeValue[0]).format('YYYY-MM-DD'));
};

/**
 * 是否限制time输入超过3年
 * @param noLimitation   不对这个筛选器做限制
 * @param userInfo       用户是否属于白名单里
 * @returns true：限制  false：不限制
 */
export const isTimeLimitation = (noLimitation: boolean, userInfo: any) => {
  return !(noLimitation || dateWhiteUserList.includes(userInfo?.userid));
};

export type specialBooleanKeyType = 'hasHero' | 'hasVideoTag' | 'hasKolTags';

const specialFilter: {
  [key: string]: Array<{
    key: specialBooleanKeyType;
    index: number;
    filter: FilterKeys;
  }>;
} = {
  u10000000066: [
    {
      key: 'hasHero',
      index: 3,
      filter: 'hero',
    },
  ],
  // uef4312b08dde07820374dd57086ea2fb: [
  //   {
  //     key: 'hasHero',
  //     index: 3,
  //     filter: 'NIKKE',
  //   },
  // ],
  u477a9f3809a0b7af26ae6383139dd66a: [
    {
      key: 'hasVideoTag',
      index: -1,
      filter: 'videoTag',
    },
  ],
  e11000000311: [
    {
      key: 'hasKolTags',
      index: 0,
      filter: 'kolTags',
    },
  ],
  u10000000022: [
    {
      key: 'hasKolTags',
      index: 0,
      filter: 'kolTags',
    },
  ],
  e11000000333: [
    {
      key: 'hasKolTags',
      index: 0,
      filter: 'kolTags',
    },
    {
      key: 'hasKolTags',
      index: -1,
      filter: 'postStream',
    },
  ],
};

/**
 * 特殊游戏添加一些特殊过滤器
 * @param filters  所有的过滤器列表
 * @param gameId   游戏id
 * @param flagMap  是否添加过滤器的标识符
 */
export const addSpecialFilter = (
  filters: Array<FilterKeys>,
  gameId: string,
  flagMap: { [key in specialBooleanKeyType]?: boolean },
) => {
  const gameSpecialFilter = specialFilter[gameId];
  if (gameSpecialFilter) {
    const len = filters.length;
    gameSpecialFilter.forEach(({ key, index, filter }) => {
      if (flagMap[key]) {
        const i = index <= 0 ? len + index : index;
        filters.splice(i, 0, filter);
      }
    });
  }
};

/**
 * isKeywordFilter是作为区分hashtag和keywordFilter过滤器的,所以在这个位置过滤掉
 * allCompetitorGame是作为区分是否全选了竞品游戏的区分,所以在这个位置过滤掉
 * @param filterValue
 * @returns
 */
const filterExcessiveKey = (filterValue: any) => {
  const {
    isKeywordFilter,
    allCompetitorGame,
    keywordActiveId,
    keywordCustomInput,
    keywordSearchType,
    topicType,
    ...otherFilter
  } = filterValue;

  return otherFilter;
};

/**
 * 该组件暂时不会启用，所以去掉
 * 处理掉EngagementSelect筛选器参数
 * 当全选，给后端返回空数组，因为不能让判断[],和满数组状态一致问题， 所以不能在筛选器中处理
 * @param filterValue
 * @returns
 */
const dealwithEngagementValue = (filterValue: any) => {
  Object.keys(targetNumMap).map((key) => {
    if (filterValue[key]?.length === targetNumMap[key]) {
      filterValue[key] = [];
    }
  });
  return filterValue;
};

const dealwithExcludeChannel = (filter: any) => {
  const { exclude_channels, channel_news } = filter;
  if (exclude_channels) {
    if (channel_news) {
      filter.channel_news = [];
    } else {
      filter.channel = [];
    }
  }
  return filter;
};

export const finallySpecialValue = (filterValue: any) => {
  // 过滤掉一些辅助参数
  const value = filterExcessiveKey(filterValue);
  // 当出现排除渠道，那么就将新闻渠道重置为空
  return dealwithExcludeChannel(value);
};

export function getNoSelectAndSelectValue({
  value,
  originalOptions,
  getRealValue = (item: string) => item,
}: {
  value: Array<string>;
  originalOptions: Array<any>;
  getRealValue?: (item: string) => string;
}) {
  if (Array.isArray(value)) {
    if (value.length === 1 && value[0] === '-99999') {
      return [];
    } else if (value.length === 0) {
      return [...originalOptions];
    }
    return value.map(getRealValue);
  }

  return [...originalOptions];
}

export function getStartAndEndValue(
  startRange: number | string,
  endRange: number | string,
) {
  const s = typeof startRange === 'string' ? Number(startRange) : startRange;
  const e = typeof endRange === 'string' ? Number(endRange) : endRange;
  if (s > e) return [e, s];

  return [s, e];
}

export function mergeExcludeChannel(
  filter: any,
  excludeChannels: Array<string>,
) {
  const { exclude_channels, ...otherFilter } = filter;

  if (exclude_channels) {
    return {
      ...otherFilter,
      exclude_channels: [...exclude_channels, ...excludeChannels],
    };
  }

  return {
    ...otherFilter,
    exclude_channels: excludeChannels,
  };
}
